import moment from 'moment';
import { useSelector } from 'react-redux';

import { ReceivedOffers } from 'connect/saga/handlers/offer/received-offers';
import { selectDataClinic, selectDataPatient, selectInsuranceListForCountry } from 'connect/selectors';
import { CheckoutStateHandler } from 'pages/checkout/connect/state-handlers';
import { CheckoutState } from 'pages/checkout/connect/types';
import { useOfferService } from 'services/OfferService';

/**
 * Determines if the clinic allows offers.
 *
 * If there is no active offer, this returns true,
 * otherwise it returns the clinic's allowOffers flag.
 */
export const useIsOfferAllowed = (): boolean => {
  const clinic = useSelector(selectDataClinic);
  const offerService = useOfferService();

  // If there is no active offer, we allow the offer
  if (!offerService.hasActiveOffer) {
    return true;
  }

  return Boolean(clinic.allowOffers);
};

export enum OfferEligibilityReason {
  UNDER_18 = 'UNDER_18',
  GOVT_INSURANCE = 'GOVT_INSURANCE',
  ALREADY_RECEIVED = 'ALREADY_RECEIVED',
}

type OfferEligibility = {
  isEligible: true;
  denyReason?: never;
} | {
  isEligible: false;
  denyReason: OfferEligibilityReason;
};

/**
 * Determines if the patient is eligible for any offer.
 *
 * Note:
 * Do not use the return value directly in any hook dependencies as it changes on every render.
 * Instead, use the needed property as a separate dependency in the hook.
 *
 * @example
 * const eligibility = useOfferEligibility();
 *
 * // Good because a specific value is used as a dependency
 * const something = useMemo(() => eligibility.isEligible, [eligibility.isEligible]);
 *
 * // Bad because the entire object is used as a dependency
 * const something = useMemo(() => eligibility, [eligibility]);
 */
export const useOfferEligibility = (): OfferEligibility => {
  const patient = useSelector(selectDataPatient);
  const checkoutState = useSelector<any, CheckoutState>(CheckoutStateHandler.selector);
  const insuranceList = useSelector(selectInsuranceListForCountry);
  const receivedOffers = useSelector(ReceivedOffers.value!.selector);

  // Check if the patient is under 18
  const patientAge = patient.dateOfBirth ? moment.utc().diff(moment.utc(patient.dateOfBirth), 'years') : null;
  if (patientAge && patientAge < 18) {
    return { isEligible: false, denyReason: OfferEligibilityReason.UNDER_18 };
  }

  // Check if the patient stated they have government insurance
  const hasInsurance = Boolean(checkoutState?.insurance?.hasInsurance);
  const { id: insuranceProviderId } = checkoutState?.insurance?.provider ?? {};

  const fullInsuranceProvider = (hasInsurance && insuranceProviderId)
    ? insuranceList.find((insurance: any) => insurance.id === insuranceProviderId)
    : null;

  if (fullInsuranceProvider?.isSocialAssistance) {
    return { isEligible: false, denyReason: OfferEligibilityReason.GOVT_INSURANCE };
  }

  // Check if the patient has already received an offer in the last 2 years
  if (receivedOffers?.length > 0) {
    return { isEligible: false, denyReason: OfferEligibilityReason.ALREADY_RECEIVED };
  }

  // All checks passed, the offer is eligible
  return { isEligible: true };
};
